import React, { useState } from 'react'
import { Table, TableBody, TableCell, TableHead, Typography, Chip, Box, Tooltip } from '@mui/material'
import { Cancel as CancelIcon } from '@mui/icons-material'
import { OrderDetails, cancelOrder } from 'src/common/api'
import { ChainName, CHAIN_NATIVE_TOKEN, OrderStatus } from 'src/common/static'
import { alertNotification } from 'src/ui'
import CancelOrderModal from './CancelOrderModal'
import * as S from './styles'
import { logEvent } from '../../analytics/analytics'

export interface OrdersTableProps {
  orders: OrderDetails[]
  onRefresh: () => Promise<void>
}

const formatTableDate = (ISOdate: string): string => {
  const date = new Date(ISOdate)
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  const year = String(date.getFullYear()).slice(-2)
  const hours = date.getHours()
  const minutes = String(date.getMinutes()).padStart(2, '0')
  const ampm = hours >= 12 ? 'PM' : 'AM'
  const displayHours = hours % 12 || 12
  return `${month}/${day}/${year} ${displayHours}:${minutes}${ampm}`
}

const getStatusColor = (status: string): 'default' | 'primary' | 'success' | 'error' => {
  switch (status) {
    case 'PROCESSED':
      return 'success'
    case 'FAILED':
      return 'error'
    case 'INITIATED':
    case 'QUEUED':
      return 'primary'
    default:
      return 'default'
  }
}

const OrdersTable: React.FC<OrdersTableProps> = ({ orders, onRefresh }: OrdersTableProps) => {
  const [cancelModalOpen, setCancelModalOpen] = useState(false)
  const [selectedOrder, setSelectedOrder] = useState<OrderDetails | null>(null)
  // eslint-disable-next-line @typescript-eslint/no-unused-vars, no-unused-vars
  const [loading, setLoading] = useState(false)

  const handleCancelClick = (order: OrderDetails) => {
    setSelectedOrder(order)
    setCancelModalOpen(true)
  }

  const handleCancelOrder = async (orderId: number | string, orgId: string) => {
    try {
      setLoading(true)
      await cancelOrder(orderId.toString(), orgId)
      alertNotification('Order cancelled successfully', 'success')
      // Refresh orders to show updated status
      await onRefresh()
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (err: any) {
      const errorMessage =
        err?.response?.data?.message || 'Failed to cancel order. Please try again or contact support.'
      console.error('Cancel order error:', err?.response?.data || err)
      alertNotification(errorMessage, 'error')
    } finally {
      setLoading(false)
    }
  }

  const handleCancelConfirm = () => {
    if (selectedOrder && selectedOrder.organizationId) {
      handleCancelOrder(selectedOrder.id, selectedOrder.organizationId)
    }
    setCancelModalOpen(false)
    setSelectedOrder(null)
    logEvent('button_click', { buttonName: 'Cancel order' })
  }

  const handleCancelClose = () => {
    setCancelModalOpen(false)
    setSelectedOrder(null)
  }

  const canCancelOrder = (status: string): boolean => {
    return status === 'INITIATED'
  }

  return (
    <>
      <Table>
        <TableHead>
          <S.StyledTableRow className="header-row">
            <S.StyledTableCell className="header-cell">Order Type</S.StyledTableCell>
            <S.StyledTableCell className="header-cell">Asset</S.StyledTableCell>
            <S.StyledTableCell className="header-cell">Chain</S.StyledTableCell>
            <S.StyledTableCell className="header-cell">Amount</S.StyledTableCell>
            <S.StyledTableCell className="header-cell">Order ID</S.StyledTableCell>
            <S.StyledTableCell className="header-cell">Date</S.StyledTableCell>
            <S.StyledTableCell className="header-cell">Status</S.StyledTableCell>
          </S.StyledTableRow>
        </TableHead>
        <TableBody>
          {orders.map(
            (order) =>
              order && (
                <S.StyledTableRow key={order.id}>
                  <TableCell>
                    <Typography variant="body2">{order.type === 'MINT' ? 'Mint' : 'Burn'}</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{order.transactions[0]?.asset || 'N/A'}</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {order.transactions[0]?.chain
                        ? CHAIN_NATIVE_TOKEN[order.transactions[0].chain as ChainName]
                        : 'N/A'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      ${order.fiatAmount} {order.fiatCurrency}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                      {order.id}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{formatTableDate(order.createdAt)}</Typography>
                  </TableCell>
                  <TableCell>
                    <Box display="flex" alignItems="center" gap={1}>
                      <Chip
                        label={OrderStatus[order.status] || order.status}
                        size="small"
                        color={getStatusColor(order.status)}
                        sx={(theme) => ({
                          backgroundColor:
                            order.status === 'CANCELLED'
                              ? theme.palette.grey[300]
                              : order.status === 'FAILED'
                              ? theme.palette.error.light
                              : order.status === 'PROCESSED'
                              ? theme.palette.success.light
                              : order.status === 'INITIATED'
                              ? theme.palette.primary[99]
                              : order.status === 'QUEUED'
                              ? theme.palette.primary[95]
                              : theme.palette.primary.light,
                          color:
                            order.status === 'CANCELLED'
                              ? theme.palette.grey[700]
                              : order.status === 'FAILED'
                              ? theme.palette.error.dark
                              : order.status === 'PROCESSED'
                              ? theme.palette.success.dark
                              : order.status === 'INITIATED'
                              ? theme.palette.primary[60]
                              : order.status === 'QUEUED'
                              ? 'white'
                              : theme.palette.primary.dark
                        })}
                      />
                      {canCancelOrder(order.status) && (
                        <Tooltip title="Cancel Order" arrow>
                          <S.CancelIconButton
                            className="cancel-icon"
                            size="small"
                            onClick={() => handleCancelClick(order)}
                          >
                            <CancelIcon fontSize="small" />
                          </S.CancelIconButton>
                        </Tooltip>
                      )}
                    </Box>
                  </TableCell>
                </S.StyledTableRow>
              )
          )}
        </TableBody>
      </Table>

      <CancelOrderModal
        open={cancelModalOpen}
        order={selectedOrder}
        onClose={handleCancelClose}
        onConfirm={handleCancelConfirm}
      />
    </>
  )
}

export default OrdersTable
